import React, { useEffect, useState } from "react";
import { useTranslation } from 'react-i18next';
import '../../CSS/footer.css';
import emb1 from '../../images/emb1.png';
import { FaPhone, FaEnvelope, FaMapMarkerAlt } from 'react-icons/fa';
import { HiOutlineLocationMarker, HiOutlinePhone, HiOutlineMail } from "react-icons/hi";
import { useLanguageData } from '../../hooks/useLanguageData';

const Footer = () => {
  const { t } = useTranslation();
  const [embassy, setEmbassy] = useState(null);
  const [menuItems, setMenuItems] = useState([]);
  const [footerButtons, setFooterButtons] = useState([]);

  // Hook pour les données de l'ambassade
  const { 
    data: embassyResponse, 
    loading, 
    error 
  } = useLanguageData('/api/v1/embassy');

  // Hook pour les menus du footer (navigation horizontale)
  const { 
    data: menuResponse, 
    loading: menuLoading 
  } = useLanguageData('/api/v1/embassy/useful-links-category?slugCategory=links-nav-usa');

  // Hook pour les boutons du footer (Contactez-nous et Prendre rendez-vous)
  const { 
    data: footerButtonsResponse, 
    loading: footerButtonsLoading 
  } = useLanguageData('/api/v1/embassy/useful-links-category?slugCategory=lien_footer');

  // Traiter les données de l'ambassade
  useEffect(() => {
    if (embassyResponse?.data) {
      setEmbassy(embassyResponse.data);
    }
  }, [embassyResponse]);

  // Traiter les données des menus
  useEffect(() => {
    if (menuResponse?.data && Array.isArray(menuResponse.data)) {
      // Trier les menus par ordre basé sur le numéro dans le slug
      const sortedMenus = [...menuResponse.data].sort((a, b) => {
        const numA = parseInt(a.slug.replace(/\D/g, '')) || 0;
        const numB = parseInt(b.slug.replace(/\D/g, '')) || 0;
        return numA - numB;
      });
      
      // Filtrer uniquement les menus principaux (ceux avec slug commençant par "menu")
      // et exclure le dernier (menu8)
      const mainMenus = sortedMenus.filter(item => 
        item.slug.startsWith('menu') && item.slug !== 'menu8'
      );
      setMenuItems(mainMenus);
    }
  }, [menuResponse]);

  // Traiter les données des boutons du footer (Contactez-nous et Prendre rendez-vous)
  useEffect(() => {
    if (footerButtonsResponse?.data && Array.isArray(footerButtonsResponse.data)) {
      console.log("📊 Données boutons footer reçues:", footerButtonsResponse);
      
      // Organiser les boutons par slug pour un accès facile
      const buttonsMap = {};
      footerButtonsResponse.data.forEach(button => {
        buttonsMap[button.slug] = button;
      });
      setFooterButtons(buttonsMap);
    }
  }, [footerButtonsResponse]);

  // Récupérer les informations du bouton Contactez-nous
  const contactButton = footerButtons['menu1'] || null;
  
  // Récupérer les informations du bouton Prendre rendez-vous
  const appointmentButton = footerButtons['menu2'] || null;

  // Composant de spinner de chargement
  const LoadingSpinner = () => (
    <div style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '60px'
    }}>
      <div style={{
        width: "20px",
        height: "20px",
        border: "2px solid #f3f3f3",
        borderTop: "2px solid #1a3c5e",
        borderRadius: "50%",
        animation: "spin 1s linear infinite",
      }}></div>

      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  );

  // Message d'information indisponible traduit
  const InfoIndisponible = () => (
    <span style={{
      color: '#999',
      fontSize: '14px',
      fontStyle: 'italic',
      padding: '5px 0'
    }}>
      {t('common.infoUnavailable')}
    </span>
  );

  return (
    <footer className="footer">
      {/* Section principale du footer */}
      <div className="footer-main">
        {/* Première ligne : Logo et navigation horizontale */}
        <div className="footer-top-row">
          <div className="footer-logo-section">
            <img src={emb1} alt={t('footer.logoAlt')} className="footer-logo" />
          </div>
          <div className="vertical-line"></div>

          <div className="footer-nav-section">
            <div className="footer-nav">
              {menuLoading ? (
                <LoadingSpinner />
              ) : menuItems.length > 0 ? (
                menuItems.map((item, index) => (
                  <a 
                    key={item.id || index} 
                    href={item.linkAccess || "#"}
                    target={item.linkAccess?.startsWith('http') ? "_blank" : "_self"}
                    rel={item.linkAccess?.startsWith('http') ? "noopener noreferrer" : ""}
                    style={{textTransform:'uppercase'}}
                  >
                    {item.name}
                  </a>
                ))
              ) : (
                // Fallback si pas de données API
                <>
                  <a href="/Benin-au-Qatar">{t('footer.nav.embassy')}</a>
                  <a href="/Articles">{t('footer.nav.news')}</a>
                  <a href="#">{t('footer.nav.services')}</a>
                  <a href="/opportunites-investissements">{t('footer.nav.opportunities')}</a>
                  <a href="/visiter-benin">{t('footer.nav.visitBenin')}</a>
                  <a href="/visiter-doha">{t('footer.nav.travelQatar')}</a>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Deuxième ligne : Informations et boutons */}
        <div className="footer-bottom-row">
          {/* Colonne de gauche - Informations de contact */}
          <div className="footer-contact-info">
            <h4 className="section-subtitle">{t('footer.embassyTitle')}</h4>
            
            {loading ? (
              <LoadingSpinner />
            ) : embassy ? (
              <>
                <p className="contact-detail">
                  <HiOutlineLocationMarker className="icon" />
                  {embassy.adresse || <InfoIndisponible />}
                </p>

                <p className="contact-detail">
                  <HiOutlinePhone className="icon" />
                  {embassy.contactPhone || <InfoIndisponible />}
                </p>

                <p className="contact-detail">
                  <HiOutlineMail className="icon" />
                  {embassy.mailEmbassy || <InfoIndisponible />}
                </p>
              </>
            ) : (
              // Affiche "Information indisponible" pour chaque champ
              <>
                <p className="contact-detail">
                  <HiOutlineLocationMarker className="icon" />
                  <InfoIndisponible />
                </p>

                <p className="contact-detail">
                  <HiOutlinePhone className="icon" />
                  <InfoIndisponible />
                </p>

                <p className="contact-detail">
                  <HiOutlineMail className="icon" />
                  <InfoIndisponible />
                </p>
              </>
            )}
          </div>

          {/* Colonne de droite - Boutons dynamiques depuis le backend */}
          <div className="footer-actions">
            {footerButtonsLoading ? (
              <LoadingSpinner />
            ) : (
              <>
                {/* Bouton Contactez-nous */}
                {contactButton && (
                  <a 
                    className="btn-contact1" 
                    style={{ textDecoration: "none" }} 
                    href={contactButton.linkAccess || "/formulaire-contact"}
                    target={contactButton.linkAccess?.startsWith('http') ? "_blank" : "_self"}
                    rel={contactButton.linkAccess?.startsWith('http') ? "noopener noreferrer" : ""}
                  >
                    {contactButton.name || t('footer.contactUs')}
                  </a>
                )}
                
                {/* Bouton Prendre rendez-vous */}
                {appointmentButton && (
                  <a 
                    className="btn-rdv px-5" 
                    style={{ textDecoration: "none" }} 
                    href={appointmentButton.linkAccess || "/"}
                    target={appointmentButton.linkAccess?.startsWith('http') ? "_blank" : "_self"}
                    rel={appointmentButton.linkAccess?.startsWith('http') ? "noopener noreferrer" : ""}
                  >
                    {appointmentButton.name || t('footer.makeAppointment')}
                  </a>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Section bas du footer */}
      <div className="footer-bottom">
        <p className="copyright">
          {embassy ? embassy.name : t('common.infoUnavailable')}
        </p>
        <p className="legal">{t('footer.legal')}</p>
      </div>
    </footer>
  );
};

export default Footer;